import { type ReactNode, useEffect, useRef, useState } from "react";
import { Text, XStack, YStack, Stack, Spinner } from "tamagui";
import { RiAddLine, RiSubtractLine } from "react-icons/ri";
import InlineLoading from "@components/loading/InlineLoading";
import { colors } from "@styles/colors";

/**
 * Skin V2 的展示層原子元件（純樣式，無業務邏輯）。
 * 只有 isGasSkinV2() 為真的地方才會用到；V1 完全不經過這裡。
 */

/** V2 色票：沿用專案主色 #2196f3，只補這一版需要的中性色與邊線。 */
export const v2 = {
  primary: colors.primary,
  primarySoft: "#EAF4FE",
  primarySoftBorder: "#BBDCFA",
  card: "#FFFFFF",
  cardBorder: "#E3EDF8",
  divider: "#F0F3F8",
  title: "#1F2937",
  /** 區塊標題用的灰階（比 title 輕，白卡上仍有 4.8:1 對比，符合 AA）。 */
  label: "#64748B",
  body: "#374151",
  sub: "#8A94A6",
  chipBg: "#F3F5F9",
  chipText: "#5F6C7B",
  disabled: "#C7CDD8",
  disabledBg: "#EEF1F6",
  /** 淡霧頁（見 useHazePage）的頁首字色：深藍而非墨黑，才和頂端那層淡藍接得起來。 */
  hazeInk: "#12447e",
  hazeSub: "#6d7c8d",
  hazeAcc: "#2f7fd4",
};

/** 區塊化白卡：扁平、細邊線、圓角 16（對比 V1 的重陰影卡）。 */
export const SkinCard = ({ children, id, padded = true }: { children: ReactNode; id?: string; padded?: boolean }) => (
  <YStack
    id={id}
    /* 換頁時錯開進場的單位（見 all.scss 的 .gas-page-stagger）。
       延遲由兄弟選擇器算，卡片不必自己知道排第幾張。 */
    className="gas-stagger"
    backgroundColor={v2.card}
    borderRadius={24}
    px={padded ? 18 : 0}
    py={padded ? 18 : 0}
    style={{ boxShadow: "0 2px 10px -4px rgba(21, 101, 192, 0.10)" }}>
    {children}
  </YStack>
);

/** 卡內區塊標題：只有粗體字（參考稿的「選擇板塊 / 筆記標題 / 附件」）。 */
export const SkinSectionLabel = ({ label, required = false, hint }: { label: string; required?: boolean; hint?: string }) => (
  <XStack ai="baseline" gap={6} mb={6}>
    {/* 明寫 lineHeight：預設行高（約 1.5em）會在標題上下各多出 3~4px 看不見的空間，
        卡片裡標題多的時候（配送資訊卡有三段）疊起來就是一段明顯的空隙。 */}
    <Text fontSize={13.5} lineHeight={16} fontWeight="700" color={v2.label}>
      {label}
    </Text>
    {required && (
      <Text fontSize={12} color={colors.danger} fontWeight="700">
        *
      </Text>
    )}
    {hint && (
      <Text fontSize={11.5} color={v2.sub}>
        {hint}
      </Text>
    )}
  </XStack>
);

/** 卡內分隔線（區塊之間）。 */
/**
 * 值變動時回傳一次性的跳動 class（見 all.scss 的 gas-total-tick）。
 *
 * 給金額這類「在畫面另一端默默改變」的數字用：加購商品時視線在商品上，
 * 不跳一下就不會發現底下的錢已經變了。回傳 undefined 而非空字串，
 * 是為了讓呼叫端可以直接放進 className 而不會多出一個空 class。
 */
export const useValueTick = (value: number | string): string | undefined => {
  const [on, setOn] = useState(false);
  const prev = useRef(value);
  useEffect(() => {
    if (value === prev.current) return;
    prev.current = value;
    setOn(true);
    const t = setTimeout(() => setOn(false), 360);
    return () => clearTimeout(t);
  }, [value]);
  return on ? "gas-total-tick" : undefined;
};

export const SkinDivider = ({ my = 14 }: { my?: number }) => <YStack height={1} backgroundColor={v2.divider} my={my} />;

/** Pill chip：未選＝灰底灰字，選中＝白底藍框藍字（參考稿「求助 / 真題 / 經驗」）。 */
export const SkinChip = ({
  label,
  selected,
  disabled = false,
  onPress,
}: {
  label: string;
  selected: boolean;
  disabled?: boolean;
  onPress?: () => void;
}) => (
  <XStack
    ai="center"
    jc="center"
    px={16}
    py={8}
    borderRadius={999}
    borderWidth={1.5}
    borderColor={selected ? v2.primary : "transparent"}
    backgroundColor={selected ? v2.card : v2.chipBg}
    opacity={disabled ? 0.45 : 1}
    cursor={disabled ? "not-allowed" : "pointer"}
    pressStyle={disabled ? undefined : { opacity: 0.75 }}
    onPress={disabled ? undefined : onPress}>
    <Text fontSize={14} fontWeight={selected ? "700" : "500"} color={selected ? v2.primary : v2.chipText}>
      {label}
    </Text>
  </XStack>
);

/**
 * 分頁籤（參考稿的「考研 / 四六級」）。
 * tone="dark"：落在淺色頁面背景上（深字＋藍色底線）；tone="light"：落在藍色 header 上（白字＋白底線）。
 */
export const SkinTabs = ({
  tabs,
  value,
  onChange,
  tone = "dark",
}: {
  tabs: { key: string; label: string }[];
  value: string;
  onChange: (key: string) => void;
  /** "ink"：淡霧底（優惠券頁）——底太亮，白字讀不到，改用深字＋品牌藍的選中色。 */
  tone?: "dark" | "light" | "ink";
}) => {
  const light = tone === "light";
  const ink = tone === "ink";
  /* 外層不留左內距：分頁籤要和頁面內容（標題、卡片、帳戶 chips）切齊同一條左邊界。
     原本的 px=2 是坐在藍帶上時為了讓白底線不貼邊，換成淡霧底之後那 2px 反而讓整排字
     比上一行的 chips 內縮，看起來像沒對齊。 */
  return (
    <XStack gap={22}>
      {tabs.map((t) => {
        const active = t.key === value;
        const color = light ? "#fff" : ink ? (active ? "#2f7fd4" : "#7d8895") : active ? v2.title : v2.sub;
        return (
          <YStack key={t.key} ai="center" gap={5} cursor="pointer" pressStyle={{ opacity: 0.7 }} onPress={() => onChange(t.key)}>
            {/* 分頁籤字級刻意小於標題列（15 < 17）：標題是「這是哪一頁」，分頁籤是頁內的次級切換，
                兩者同級（原本都是 16 粗體）會讓標題被壓過去。未選中再淡一階拉開對比。 */}
            <Text fontSize={15} fontWeight={active ? "800" : "500"} color={color} opacity={light && !active ? 0.6 : 1}>
              {t.label}
            </Text>
            <YStack height={3} width={active ? 20 : 0} borderRadius={999} backgroundColor={light ? "#fff" : ink ? "#2f7fd4" : v2.primary} />
          </YStack>
        );
      })}
    </XStack>
  );
};

/** 可點擊的列（右側 ›），參考稿的「話題」列。 */
export const SkinNavRow = ({
  label,
  value,
  placeholder,
  danger = false,
  onPress,
}: {
  label?: string;
  value?: string | null;
  placeholder?: string;
  danger?: boolean;
  onPress?: () => void;
}) => (
  <XStack ai="center" jc="space-between" gap={10} cursor={onPress ? "pointer" : "default"} pressStyle={onPress ? { opacity: 0.7 } : undefined} onPress={onPress}>
    <YStack flex={1} minWidth={0}>
      {label && (
        <Text fontSize={12.5} color={v2.sub} mb={3}>
          {label}
        </Text>
      )}
      <Text fontSize={15} fontWeight={value ? "700" : "400"} color={value ? v2.title : danger ? colors.danger : v2.sub} numberOfLines={1}>
        {value || placeholder}
      </Text>
    </YStack>
    {onPress && (
      <Text fontSize={20} color={v2.sub} flexShrink={0}>
        ›
      </Text>
    )}
  </XStack>
);

/**
 * 瓦斯訂購 V2 的按鈕層級（整個流程只有這三種，不要再各自寫樣式）：
 *   primary   實心藍膠囊（扁平，不用漸層／陰影）。**每頁只有一顆**，代表該頁的主要動作
 *             （結帳／加入購物車／確認下單）。
 *   secondary 淡藍底藍字膠囊，與 primary 同高，成對出現時放左邊（取消／返回修改／返回選購）。
 *   inline    secondary 的小尺寸版，用在列表列內的動作（商品列的「加入」）。
 * 只有 primary 是實心藍 → 掃一眼就知道這頁要按哪裡。
 */
export type SkinButtonVariant = "primary" | "secondary" | "inline";

export const SkinButton = ({
  label,
  variant = "primary",
  disabled = false,
  loading = false,
  flex,
  icon,
  onPress,
}: {
  label: string;
  variant?: SkinButtonVariant;
  disabled?: boolean;
  loading?: boolean;
  flex?: number;
  /** 置於文字前的圖示（inline 變體用來補「可點」的暗示）。 */
  icon?: ReactNode;
  onPress?: () => void;
}) => {
  const inline = variant === "inline";
  const solid = variant === "primary";
  return (
    <XStack
      flex={flex}
      ai="center"
      jc="center"
      gap={inline ? 4 : 8}
      py={inline ? 8 : 13}
      px={inline ? 14 : undefined}
      borderRadius={999}
      backgroundColor={solid ? (disabled ? v2.disabledBg : v2.primary) : v2.primarySoft}
      borderWidth={solid ? 0 : 1}
      borderColor={solid ? undefined : v2.primarySoftBorder}
      opacity={disabled && !solid ? 0.5 : 1}
      cursor={disabled ? "default" : "pointer"}
      /* pressStyle 只改透明度，在實心的主要鈕上幾乎看不出來——
         gas-press 補一個微沉的位移，那才是手指按得到的回饋。 */
      className={disabled ? undefined : "gas-press"}
      pressStyle={disabled ? undefined : { opacity: 0.7 }}
      onPress={disabled ? undefined : onPress}>
      {loading && <Spinner size="small" color={solid ? "#fff" : v2.primary} />}
      {!loading && icon}
      <Text
        fontSize={inline ? 14 : 15.5}
        fontWeight={solid ? "800" : "700"}
        color={solid ? (disabled ? v2.disabled : "#fff") : v2.primary}>
        {label}
      </Text>
    </XStack>
  );
};

/**
 * 數量步進器：把「− 值 +」收進一顆膠囊。
 * 原本是兩顆白底淺灰框的圓鈕配灰圖示，放在白卡上幾乎看不出可按；
 * 收進有底色的膠囊後點擊區與可按性都明確，也跟這一版的膠囊語彙一致。
 *
 * 數字變化時帶方向彈入（加往上、減往下，見 all.scss 的 gas-qty-*）：方向本身就是回饋，
 * 使用者不必回頭確認數字也知道自己按到哪一邊。這在網路慢、qty 要等 API 回來才更新的
 * 情境特別重要——動畫是在「值真的變了」時才播，所以它同時也是「後端收到了」的訊號。
 */
export const SkinStepper = ({
  value,
  onDec,
  onInc,
  disabled = false,
  loading = false,
  bg = v2.chipBg,
}: {
  value: number;
  onDec?: () => void;
  onInc?: () => void;
  disabled?: boolean;
  loading?: boolean;
  /** 底色。放在已經是淺灰底的區塊上時傳白色，才不會糊成一片。 */
  bg?: string;
}) => {
  const [dir, setDir] = useState<"up" | "down" | null>(null);
  const prev = useRef(value);

  useEffect(() => {
    if (value === prev.current) return;
    setDir(value > prev.current ? "up" : "down");
    prev.current = value;
    // 動畫結束就把 class 拿掉，否則同一個方向連按兩次不會重播
    const t = setTimeout(() => setDir(null), 300);
    return () => clearTimeout(t);
  }, [value]);

  return (
    <XStack ai="center" backgroundColor={bg} borderRadius={999} px={4} py={3} flexShrink={0}>
      <XStack
        className={disabled ? undefined : "gas-qty-btn"}
        width={28}
        height={28}
        ai="center"
        jc="center"
        borderRadius={999}
        cursor={disabled ? "default" : "pointer"}
        pressStyle={disabled ? undefined : { opacity: 0.5 }}
        onPress={disabled ? undefined : onDec}>
        <RiSubtractLine size={15} color={v2.chipText} />
      </XStack>
      {/* overflow hidden：數字是上下滑進來的，超出框的那一段要切掉才像滾動 */}
      <XStack minWidth={26} jc="center" ai="center" position="relative" overflow="hidden">
        <Text fontSize={15} fontWeight="700" color={v2.title} opacity={loading ? 0.3 : 1}>
          <span className={`gas-qty-num${dir ? ` gas-qty-num-${dir}` : ""}`}>{value}</span>
        </Text>
        {loading && (
          <Stack position="absolute">
            <InlineLoading />
          </Stack>
        )}
      </XStack>
      <XStack
        className={disabled ? undefined : "gas-qty-btn"}
        width={28}
        height={28}
        ai="center"
        jc="center"
        borderRadius={999}
        cursor={disabled ? "default" : "pointer"}
        pressStyle={disabled ? undefined : { opacity: 0.5 }}
        onPress={disabled ? undefined : onInc}>
        <RiAddLine size={15} color={v2.chipText} />
      </XStack>
    </XStack>
  );
};

/** 底部固定雙鈕列：左＝外框次要鈕，右＝實心主要鈕（參考稿「預覽 / 發布」）。 */
export const SkinBottomBar = ({
  secondaryLabel,
  onSecondary,
  primaryLabel,
  onPrimary,
  primaryDisabled = false,
  primaryLoading = false,
  nested = false,
}: {
  secondaryLabel?: string;
  onSecondary?: () => void;
  primaryLabel: string;
  onPrimary?: () => void;
  primaryDisabled?: boolean;
  primaryLoading?: boolean;
  /** 已被外層 sticky 容器套上 .gas-skin-v2-bottom-fade 時傳 true，避免遮罩疊兩層、上緣留白過多。 */
  nested?: boolean;
}) => (
  <YStack
    // 底部按鈕列是流程的最後一個元素，跟著卡片一起錯開進場才不會突然出現
    className={`gas-stagger${nested ? "" : " gas-skin-v2-bottom-fade"}`}
    backgroundColor="transparent"
    px={4}
    pt={nested ? 6 : 14}
    pb={4}
    // mt auto：sticky 只保證「捲動時黏在底部」，內容不滿一頁時按鈕會停在內容結尾（畫面中間）。
    // 由 auto 上邊距把它推到伸展後的容器底部，長短頁的按鈕位置才一致。
    mt={nested ? 0 : "auto"}
    style={nested ? { width: "100%" } : { position: "sticky", bottom: 0, width: "100%", zIndex: 100 }}>
    <XStack gap={12} ai="center">
      {secondaryLabel && <SkinButton label={secondaryLabel} variant="secondary" flex={1} onPress={onSecondary} />}
      <SkinButton
        label={primaryLabel}
        variant="primary"
        flex={secondaryLabel ? 1.7 : 1}
        disabled={primaryDisabled}
        loading={primaryLoading}
        onPress={onPrimary}
      />
    </XStack>
  </YStack>
);
